import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ErrorBoundary } from '../shared/ErrorBoundary';
import { useNotification } from '../../contexts/NotificationContext';
import { articleLearningsService } from '../../services/articleLearningsService';
import { ArticleLearningRow } from '../../types/articleGeneration';

interface Props {
  companyId: string;
  domainId: string | null;
}

const EXCERPT = 120;
const excerpt = (text: string) => (text.length > EXCERPT ? `${text.slice(0, EXCERPT)}…` : text);

/**
 * The Learnings tab: every note an editor wrote on a highlighted passage, with
 * a switch that decides whether the note is fed to future generations.
 */
const LearningsPanel: React.FC<Props> = ({ companyId, domainId }) => {
  const { t } = useTranslation();
  const { showNotification } = useNotification();
  const [rows, setRows] = useState<ArticleLearningRow[]>([]);
  const [allDomains, setAllDomains] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    articleLearningsService
      .list(companyId, allDomains ? null : domainId)
      .then((data) => active && setRows(data))
      .catch((error) => {
        if (!active) return;
        setRows([]);
        showNotification(
          t('article.learnings_load_failed', { message: (error as Error).message }),
          'error'
        );
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [companyId, domainId, allDomains]);

  const toggleUse = async (row: ArticleLearningRow) => {
    const next = !row.use_in_generation;
    setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, use_in_generation: next } : r)));
    try {
      await articleLearningsService.setUseInGeneration(row.id, next);
    } catch (error) {
      setRows((prev) =>
        prev.map((r) => (r.id === row.id ? { ...r, use_in_generation: !next } : r))
      );
      showNotification(
        t('article.learnings_update_failed', { message: (error as Error).message }),
        'error'
      );
    }
  };

  const remove = async (row: ArticleLearningRow) => {
    if (!window.confirm(t('article.learnings_delete_confirm'))) return;
    try {
      await articleLearningsService.remove(row.id);
      setRows((prev) => prev.filter((r) => r.id !== row.id));
    } catch (error) {
      showNotification(
        t('article.learnings_update_failed', { message: (error as Error).message }),
        'error'
      );
    }
  };

  return (
    <ErrorBoundary>
      <div className="space-y-4">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <p className="text-sm text-gray-600 max-w-2xl">{t('article.learnings_intro')}</p>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={allDomains}
              onChange={(e) => setAllDomains(e.target.checked)}
            />
            {t('article.learnings_all_domains')}
          </label>
        </div>

        {!loading && rows.length === 0 ? (
          <div className="border rounded-lg p-10 text-center text-gray-500 bg-gray-50">
            {t('article.learnings_empty')}
          </div>
        ) : (
          <div className="overflow-x-auto border rounded-lg">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase text-gray-500 border-b bg-gray-50">
                  <th className="p-2">{t('article.learnings_col_date')}</th>
                  <th className="p-2">{t('article.learnings_col_section')}</th>
                  <th className="p-2">{t('article.learnings_col_selected')}</th>
                  <th className="p-2">{t('article.learnings_col_note')}</th>
                  <th className="p-2">{t('article.learnings_col_status')}</th>
                  <th className="p-2">{t('article.learnings_col_use')}</th>
                  <th className="p-2"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id} className="border-b last:border-0 align-top" data-testid="learning-row">
                    <td className="p-2 whitespace-nowrap text-gray-600">
                      <div>{new Date(row.created_at).toLocaleString()}</div>
                      {row.generation_id && (
                        <Link
                          to={`/article-generation?run=${row.generation_id}`}
                          className="text-blue-600 hover:underline text-xs"
                        >
                          {t('article.learnings_open_article')}
                        </Link>
                      )}
                    </td>
                    <td className="p-2 text-gray-700">
                      {row.section_heading || t('article.learnings_lead')}
                    </td>
                    <td className="p-2 text-gray-600 italic max-w-xs">{excerpt(row.selected_text)}</td>
                    <td className="p-2 max-w-sm">{row.note}</td>
                    <td className="p-2">
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full ${
                          row.status === 'applied'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-amber-100 text-amber-800'
                        }`}
                      >
                        {t(`article.learnings_status_${row.status}`)}
                      </span>
                    </td>
                    <td className="p-2">
                      <input
                        type="checkbox"
                        aria-label={t('article.learnings_use')}
                        checked={row.use_in_generation}
                        onChange={() => toggleUse(row)}
                      />
                    </td>
                    <td className="p-2 text-right">
                      <button
                        type="button"
                        onClick={() => remove(row)}
                        className="text-red-600 hover:underline text-xs"
                      >
                        {t('article.learnings_delete')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </ErrorBoundary>
  );
};

export default LearningsPanel;
